/* eslint-disable @typescript-eslint/member-ordering */
import {IKycPersonDataset, IKycPhone, TKycEmploymentType, TKycOccupationType} from '@sparbanken-syd/borgo-types'
import {search} from 'jmespath'
import {DateTime} from 'luxon'

/**
 * We can fiddle with this ...
 * @param input
 * @param areaCode
 */
export const convertPhoneNumber = (input = '', areaCode: string): IKycPhone => {
  let number = input.replace(/\D/g, '')
  if (number.charAt(0) !== '0') {
    number = `0${number}`
  }

  return {
    cc: areaCode,
    area: number.substring(1, 4),
    number: number.substring(4),
    type: 'MOBILE'
  }
}

const getDateFromBorgo = (year: number | null, month: number | null) => {
  let date: DateTime | null = null
  if (year && month) {
    const m = `${month}`.padStart(2, '0')
    date = DateTime.fromISO(`${year}-${m}-01`)
  }
  return date
}

const getBorgoYear = (date: DateTime<true> | null) => {
  if (date) {
    return date.year
  }
  return null
}

const getBorgoMonth = (date: DateTime | null) => {
  if (date) {
    return date.month
  }
  return null
}

export interface IArrayVal {
  val: string | number
  pos: number
}

/**
 * Describes a "Tax Country"
 */
export interface ITaxation {
  // Two-letter code
  country: string
  // Mandatory if not SE
  tin?: string | null
}

export interface IBorgoTaxation {
  country: string
  tinCode?: string
}

export interface IEmployment {
  // anställld or arbetslös
  occupation: TKycOccupationType | null
  // Tillsvidare or Visstid
  employment: TKycEmploymentType | null
  startDate: DateTime | null
  endDate: DateTime | null
  employer: string | null
  // Must be set but can be zero
  salary: number
}

export class Kyc {

  constructor(public kyc: IKycPersonDataset) {
  }


  // Aka personnummer
  get sub(): string {
    return this.getProperty('nationalIdDataset.nationalId') ?? ''
  }

  set sub(value: string) {
    this.setProperty<string>('nationalIdDataset.nationalId', value)
  }

  get firstName(): string {
    return this.getProperty('personBasicDataset.personNameDataset.preferredGivenName')
  }

  set firstName(value: string) {
    this.setProperty('personBasicDataset.personNameDataset.preferredGivenName', value)
  }

  get otherGivenNames(): string {
    return this.getProperty('personBasicDataset.personNameDataset.otherGivenNames')
  }

  set otherGivenNames(value: string) {
    this.setProperty('personBasicDataset.personNameDataset.otherGivenNames', value)
  }

  get lastName(): string {
    return this.getProperty('personBasicDataset.personNameDataset.surname')
  }

  set lastName(value: string) {
    this.setProperty('personBasicDataset.personNameDataset.surname', value)
  }

  get language(): string {
    return this.getProperty('personBasicDataset.personBiographicalDataset.language')
  }

  set language(value: string) {
    this.setProperty('personBasicDataset.personBiographicalDataset.language', value)
  }

  get country(): string {
    return this.getProperty('addresses[0].country')
  }

  set country(value: string) {
    this.setProperty('addresses[0].country', value)
  }

  get coName(): string {
    return this.getProperty('addresses[0].coName')
  }

  set coName(value: string) {
    this.setProperty('addresses[0].coName', value)
  }

  get addressLine(): string {
    return this.getProperty('addresses[0].addressLine')
  }

  set addressLine(value: string) {
    this.setProperty('addresses[0].addressLine', value)
  }

  get postalCode(): string {
    return this.getProperty('addresses[0].postalCode')
  }

  set postalCode(value: string) {
    this.setProperty('addresses[0].postalCode', value)
  }

  get postalTown(): string {
    return this.getProperty('addresses[0].location')
  }

  set postalTown(value: string) {
    this.setProperty('addresses[0].location', value)
  }

  get mobile(): string {
    const prop = this.getProperty<IKycPhone>('contactDataset.phones[0]')

    // Check if the area or number is missing or empty
    if (!prop?.area || !prop?.number) {
      return '' // No valid phone number available
    }

    // Format the phone number as "0<area><number>"
    return `0${prop.area}${prop.number}`
  }

  set mobile(value: string) {
    const areaCode = this.getProperty<string>('contactDataset.phones[0].cc')
    // Possibly convert this somewhere else...
    this.setProperty('contactDataset.phones[0]', convertPhoneNumber(value, areaCode))
  }

  get areaCode(): string {
    return this.getProperty('contactDataset.phones[0].cc')
  }

  set areaCode(value: string) {
    // Possibly convert this somewhere else...
    this.setProperty('contactDataset.phones[0].cc', value)
  }

  get privateEmail(): string {
    return this.getProperty('contactDataset.emails[0].address')
  }

  set privateEmail(value: string | null) {
    if (value === null || value === '') {
      this.setProperty('contactDataset.emails', [])
      return
    }
    this.setProperty('contactDataset.emails[0].address', value)
    this.setProperty('contactDataset.emails[0].type', 'PRIVATE')
  }

  get nationalities(): string[] {
    return this.getProperty('personBasicDataset.personBiographicalDataset.nationality')
  }

  set nationalities(value: string[]) {
    this.setProperty('personBasicDataset.personBiographicalDataset.nationality', value)
  }

  get isUsCitizen(): boolean {
    const citizens = this.getProperty<string[] | null>('personBasicDataset.personBiographicalDataset.nationality')
    return !!citizens?.includes('US')
  }

  get taxCountries(): ITaxation[] {
    const countries: ITaxation[] = [
      {
        country: this.getProperty('personTaxationDataset.mainTaxation.country'),
        tin: this.getProperty('personTaxationDataset.mainTaxation.tinCode')
      }
    ]

    this.getProperty<IBorgoTaxation[] | undefined>('personTaxationDataset.parallelTaxations')?.forEach(
      (taxation) => {
        countries.push({country: taxation.country, tin: taxation.tinCode})
      }
    )
    return countries
  }

  set taxCountries(countries: ITaxation[]) {
    // Normally there should be at least one ...
    this.setProperty('personTaxationDataset.mainTaxation.country', countries[0]?.country)
    this.setProperty('personTaxationDataset.mainTaxation.tinCode', countries[0]?.tin)
    // And we reset the others.
    this.setProperty('personTaxationDataset.parallelTaxations', [])
    for (let i = 1; i < countries.length; i++) {
      this.setProperty(`personTaxationDataset.parallelTaxations[${i - 1}].country`, countries[i].country)
      this.setProperty(`personTaxationDataset.parallelTaxations[${i - 1}].tinCode`, countries[i].tin)
    }
  }

  ////////
  get employment(): IEmployment {
    const startDate = getDateFromBorgo(this.employmentStartYear, this.employmentStartMonth)
    const endDate = getDateFromBorgo(this.employmentEndYear, this.employmentEndMonth)
    return {
      occupation: this.occupation,
      employment: this.formOfEmployment,
      startDate,
      endDate,
      employer: this.employer,
      salary: this.grossSalary
    }
  }

  set employment(employment: IEmployment) {

    this.employmentEndYear = null
    this.employmentEndMonth = null
    this.employmentStartYear = null
    this.employmentStartMonth = null
    this.employer = null
    this.formOfEmployment = null
    // This field is not used but should be nullified
    this.occupationSpecification = null

    // Must always exist and be set (Anställd, Student, Arebetslös ....)
    this.occupation = employment.occupation

    if (employment.occupation === 'EMPLOYEE') {
      this.employer = employment.employer

      // Permanent or fixed term should only be set if EMPLOYEE
      this.formOfEmployment = employment.employment!

      this.employmentStartYear = getBorgoYear(employment.startDate)
      this.employmentStartMonth = getBorgoMonth(employment.startDate)

      if (employment.employment === 'FIXED_TERM') {
        this.employmentEndYear = getBorgoYear(employment.endDate)
        this.employmentEndMonth = getBorgoMonth(employment.endDate)
      }
    }


    // Should always be present or null.
    this.grossSalary = employment.salary
  }

  get formOfEmployment() {
    return this.getProperty<TKycEmploymentType>('personRegulatoryDataset.personEmploymentDataset.formOfEmployment')
  }

  set formOfEmployment(value: TKycEmploymentType | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.formOfEmployment', value)
  }

  get occupation() {
    return this.getProperty<TKycOccupationType | null>('personRegulatoryDataset.personEmploymentDataset.occupation')
  }

  set occupation(value: TKycOccupationType | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.occupation', value)
  }

  set occupationSpecification(value: string | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.occupationSpecification', value)
  }

  get employer(): string | null {
    const employer = this.getProperty<string | null>('personRegulatoryDataset.personEmploymentDataset.employer')
    if (employer?.toLowerCase().includes('migrerad')) {
      return null
    }
    return employer
  }

  set employer(value: string | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.employer', value)
  }

  get grossSalary() {
    return this.getProperty<number>('personRegulatoryDataset.personIncomeDataset.grossSalary')
  }

  set grossSalary(value: number) {
    this.setProperty('personRegulatoryDataset.personIncomeDataset.grossSalary', value)
  }

  get employmentStartYear() {
    return this.getProperty<number | null>('personRegulatoryDataset.personEmploymentDataset.employmentStartYear')
  }

  set employmentStartYear(value: number | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.employmentStartYear', value)
  }

  get employmentStartMonth() {
    return this.getProperty<number | null>('personRegulatoryDataset.personEmploymentDataset.employmentStartMonth')
  }

  set employmentStartMonth(value: number | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.employmentStartMonth', value)
  }

  get employmentEndYear() {
    return this.getProperty<number | null>('personRegulatoryDataset.personEmploymentDataset.employmentEndYear')
  }

  set employmentEndYear(value: number | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.employmentEndYear', value)
  }

  get employmentEndMonth() {
    return this.getProperty<number | null>('personRegulatoryDataset.personEmploymentDataset.employmentEndMonth')
  }

  set employmentEndMonth(value: number | null) {
    this.setProperty('personRegulatoryDataset.personEmploymentDataset.employmentEndMonth', value)
  }

  ///

  get politicallyExposed(): string {
    return this.getProperty('personRegulatoryDataset.personPepDataset.politicallyExposed')
  }

  set politicallyExposed(value: boolean) {
    this.setProperty('personRegulatoryDataset.personPepDataset.politicallyExposed', value)
  }

  // End of it
  private getProperty<T>(path: string): T {
    return search(this.kyc, path) as T
  }

  /**
   * The below functions needs to be evolved eventually...
   */

  private setProperty<T>(path: string, value: T): void {
    const parts = path.split('.')
    this.setValue(parts, this.kyc, value)
  }

  private setValue(props: string[], item: any, value: any): void {
    if (props.length > 1) {
      this.handleMulti(props, item, value)
    } else {
      let prop = props[0]
      const arrayMatch = new RegExp(/\[(\d+)]/).exec(prop) // Check if the last property is an array element
      if (arrayMatch) {
        const index = Number.parseInt(arrayMatch[1])
        prop = prop.replace(arrayMatch[0], '')

        // Initialize the array if it doesn't exist
        /*if (!item[prop]) {
          item[prop] = []
        }*/

        // Ensure the array is long enough
        /*while (item[prop].length <= index) {
          item[prop].push(undefined)
        }*/

        // Set the value at the correct index in the array
        item[prop][index] = value
      } else {
        // If it's not an array index, set the value directly on the object
        item[props[0]] = value
      }
    }
  }

  private handleMulti(props: string[], item: any, value: any) {
    let nextProp = props[0]
    // Check if the next property is an array index

    const arrayMatch = new RegExp(/\[(\d+)]/).exec(nextProp)

    if (arrayMatch) {
      const index = Number.parseInt(arrayMatch[1])
      nextProp = nextProp.replace(arrayMatch[0], '')

      // Initialize the array if it doesn't exist
      if (!item[nextProp]) {
        item[nextProp] = []
      }

      // Ensure the array is long enough and create the objects if they don't exist
      while (item[nextProp].length <= index) {
        item[nextProp].push({}) // Create an empty object at each index
      }

      // Call for the next level
      this.setValue(props.slice(1), item[nextProp][index], value)
    } else {
      // If it's not an array index, create the nested object if it doesn't exist
      if (!item[nextProp]) {
        item[nextProp] = {}
      }
      this.setValue(props.slice(1), item[nextProp], value)
    }
  }
}
